// backdrop.ts — shared atmospheric background for every scene.
//
// Lifts the flat OLED-navy fill into a premium arcade backdrop with real depth: a multi-stop vertical
// gradient (cooler/deeper at the rim, a breath of warmth toward centre), stacked soft blooms, a faint
// drifting nebula + scanline motif, a sparse field of slow ambient motes, a subtle grid, drifting neon
// glow orbs, and an edge vignette that frames + seats the board. It is a pure renderer concern (no Core,
// no event-log) and is fully accessibility-gated: under reduced motion the orbs/motes/nebula hold static
// and nothing animates. Every layer paints SYNCHRONOUSLY in the constructor (VG-11) so the first
// composited frame already shows the full dressed field — never a blank navy seam. Scenes call
// `addBackdrop(this, mode)` once in create() and place everything else above depth -10.

import Phaser from 'phaser';
import { PALETTE } from './keys';
import { motionAllowed } from './settings';

export type BackdropMode = 'menu' | 'game' | 'result';

const TOP = 0x171c30; // deep indigo top
const MID = 0x10131f; // brand navy
const BOTTOM = 0x070810; // near-black floor
const RIM = 0x05060c; // coolest/deepest tone — used at the very top/bottom rim for edge depth

interface Orb {
  arc: Phaser.GameObjects.Arc;
  baseX: number;
  baseY: number;
  driftX: number;
  driftY: number;
  phase: number;
}

interface Mote {
  arc: Phaser.GameObjects.Arc;
  baseX: number;
  baseY: number;
  driftX: number;
  driftY: number;
  speed: number;
  phase: number;
}

export class Backdrop {
  private scene: Phaser.Scene;
  private orbs: Orb[] = [];
  private motes: Mote[] = [];
  private nebula?: Phaser.GameObjects.Graphics;
  private nebulaBaseX = 0;
  private nebulaBaseY = 0;
  private grid?: Phaser.GameObjects.Graphics;
  private ribbons?: Phaser.GameObjects.Graphics;
  private elapsed = 0;
  private container: Phaser.GameObjects.Container;

  constructor(scene: Phaser.Scene, mode: BackdropMode) {
    this.scene = scene;
    const w = scene.scale.width;
    const h = scene.scale.height;
    const motion = motionAllowed();
    // Match the flat OLED mockups: the result screen gets a near-black field (ResultScene paints its
    // own localized gold/red radial), so the shared decorative blooms/orbs/nebula are dropped there
    // and toned down everywhere else — the mockups are flat-dark, not a bright radar wash.
    const minimal = mode === 'result';
    this.container = scene.add.container(0, 0).setDepth(-10).setScrollFactor(0);

    // 1) multi-stop vertical gradient — top tint VARIES by scene so each context has its own mood (menu
    // reads inviting/cool-rich, the board reads focused/calm, results read warm-celebratory). A cooler
    // RIM stop top + bottom (instead of a single navy → navy fill) gives the field real top-to-bottom
    // depth — light pools in the middle, the edges recede.
    const topByMode: Record<BackdropMode, number> = { menu: 0x1b2142, game: 0x141a2c, result: 0x161a2e };
    const top = topByMode[mode] ?? TOP;
    // FLOOR tone VARIES by scene. On `game` the board is centred so a near-black RIM floor is GOOD
    // (premium cabinet framing, reviewed great). On `menu`/`result` the BOTTOM band carries content
    // (CTAs + summary text), so settle the floor to a LIGHTER tone there — never near-black — so those
    // bottom-edge chips/labels keep strong contrast. game keeps RIM; menu/result lift toward MID.
    const floorByMode: Record<BackdropMode, number> = { menu: 0x101626, game: RIM, result: 0x0c0e16 };
    const floor = floorByMode[mode] ?? RIM;
    // …and the lowest band's TOP stop also lifts off pure BOTTOM for menu/result so the sink is gentle.
    const sinkTop = mode === 'game' ? BOTTOM : MID;
    const g = scene.add.graphics();
    // upper rim → top tint (the field "opens up" out of a dark crown)
    g.fillGradientStyle(RIM, RIM, top, top, 1);
    g.fillRect(0, 0, w, h * 0.14);
    // top tint → mid (the lit body)
    g.fillGradientStyle(top, top, MID, MID, 1);
    g.fillRect(0, h * 0.14 - 1, w, h * 0.41 + 1);
    // mid → bottom (the field sinks)
    g.fillGradientStyle(MID, MID, BOTTOM, BOTTOM, 1);
    g.fillRect(0, h * 0.55 - 1, w, h * 0.31 + 1);
    // bottom → floor (game settles into a dark RIM floor; menu/result hold a lighter floor for CTAs)
    g.fillGradientStyle(sinkTop, sinkTop, floor, floor, 1);
    g.fillRect(0, h * 0.86 - 1, w, h * 0.14 + 1);
    this.container.add(g);

    // 2) soft blooms — a primary centre bloom (stacked low-alpha additive discs) for the arcade-cabinet
    // glow, plus a second, smaller, warmer counter-bloom offset to one side so the light isn't a single
    // symmetric blob. Both additive + very low alpha so they read as ambient light, never as a shape.
    // Result mood (gold/red) is painted by ResultScene's own radial; keep the shared bloom cool here so
    // the field stays OLED-dark (the redline) instead of washing amber.
    const bloomColor = PALETTE.player;
    const bloom = scene.add.graphics().setBlendMode(Phaser.BlendModes.ADD);
    const bcx = w / 2;
    const bcy = mode === 'menu' ? h * 0.34 : h * 0.42;
    const baseR = Math.max(w, h) * 0.5;
    const bloomA = minimal ? 0 : mode === 'game' ? 0.01 : 0.014; // calmer; result stays flat (scene draws its own radial)
    for (let i = 4; i >= 1; i--) {
      bloom.fillStyle(bloomColor, bloomA * i);
      bloom.fillCircle(bcx, bcy, (baseR * i) / 4);
    }
    // warm counter-bloom (amber) — a breath of warmth toward centre/upper field for premium depth; even
    // dimmer than the primary so it never competes with the board.
    const warmA = minimal ? 0 : mode === 'game' ? 0.006 : 0.009;
    const wcx = w * (mode === 'menu' ? 0.66 : 0.6);
    const wcy = h * 0.5;
    const warmR = Math.max(w, h) * 0.34;
    for (let i = 3; i >= 1; i--) {
      bloom.fillStyle(PALETTE.armed, warmA * i);
      bloom.fillCircle(wcx, wcy, (warmR * i) / 3);
    }
    this.container.add(bloom);

    // 3) large-scale parallax nebula + scanline motif — a single very-soft, big radial nebula blob plus a
    // few faint horizontal scanlines, all at very low alpha and big scale. Adds a sense of space/texture
    // without busy detail. Drawn into its own graphics so it can drift as one slow parallax layer (and so
    // a static version is trivially the same draw with no per-frame offset).
    this.nebula = scene.add.graphics().setBlendMode(Phaser.BlendModes.ADD);
    this.nebulaBaseX = w * 0.5;
    this.nebulaBaseY = h * (mode === 'menu' ? 0.6 : 0.66);
    this.drawNebula(w, h, mode);
    this.container.add(this.nebula);

    // 4) faint grid — a subtle play-field lattice that reads as "arcade" without competing with cells
    this.grid = scene.add.graphics();
    this.drawGrid(w, h, mode);
    this.container.add(this.grid);

    // 4b) long, faint depth ribbons — broad diagonal traces near the playfield edges. They add a
    // layered "space cabinet" read without introducing busy particles or competing with tile labels.
    this.ribbons = scene.add.graphics().setBlendMode(Phaser.BlendModes.ADD);
    this.drawDepthRibbons(w, h, mode);
    this.container.add(this.ribbons);

    // 5) ambient drifting motes — a sparse field of tiny, dim dust specks that drift slowly (parallax-ish)
    // behind the board. FEW and low-alpha by design so they add life without ever competing with answer
    // labels. Under reduced motion we keep just a handful, fully static (calm fallback). Decorative
    // placement only (pure presentation, never Core) — a cheap deterministic hash, no Math.random, so the
    // first synchronous frame is reproducible for screenshots.
    const moteCount = minimal ? 0 : motion ? (mode === 'game' ? 10 : 12) : mode === 'game' ? 3 : 4;
    const moteColors = [PALETTE.player, PALETTE.scan, PALETTE.white, PALETTE.ai];
    for (let i = 0; i < moteCount; i++) {
      const col = moteColors[i % moteColors.length]!;
      // hash-ish but stable: irrational multipliers spread the points without clustering.
      const fx = (i * 0.61803398875 + 0.13) % 1;
      const fy = (i * 0.41421356237 + 0.27) % 1;
      const mx = w * (0.04 + 0.92 * fx);
      const my = h * (0.05 + 0.9 * fy);
      const r = 0.8 + 1.6 * ((i * 0.317) % 1);
      const a = (mode === 'game' ? 0.05 : 0.08) + 0.05 * ((i * 0.53) % 1);
      const arc = scene.add.circle(mx, my, r, col, a).setBlendMode(Phaser.BlendModes.ADD);
      this.container.add(arc);
      this.motes.push({
        arc,
        baseX: mx,
        baseY: my,
        driftX: w * (0.012 + 0.02 * ((i * 0.19) % 1)),
        driftY: h * (0.02 + 0.03 * ((i * 0.29) % 1)),
        speed: 0.05 + 0.05 * ((i * 0.11) % 1),
        phase: i * 0.9,
      });
    }

    // 6) drifting neon glow orbs (player cyan + rival magenta), additive, low alpha
    const orbColors = [PALETTE.player, PALETTE.ai, PALETTE.scan, PALETTE.player];
    const orbCount = minimal ? 0 : mode === 'game' ? 3 : 4;
    for (let i = 0; i < orbCount; i++) {
      const col = orbColors[i % orbColors.length]!;
      const bx = w * (0.12 + 0.76 * ((i * 0.37 + 0.1) % 1));
      const by = h * (0.12 + 0.74 * ((i * 0.53 + 0.2) % 1));
      const r = Math.round(w * (0.06 + 0.04 * ((i * 0.29) % 1)));
      const arc = scene.add
        .circle(bx, by, r, col, mode === 'game' ? 0.03 : 0.035)
        .setBlendMode(Phaser.BlendModes.ADD);
      this.container.add(arc);
      this.orbs.push({
        arc,
        baseX: bx,
        baseY: by,
        driftX: w * (0.03 + 0.02 * ((i * 0.17) % 1)),
        driftY: h * (0.025 + 0.02 * ((i * 0.23) % 1)),
        phase: i * 1.7,
      });
    }

    // 7) edge vignette — four edge gradients darkening toward the rim so the board sits in a framed
    // "cabinet" pool of light. Mode-aware: full strength on `game` (centred board, edges are pure
    // backdrop — the strong frame reviewed great), but scaled DOWN hard on `menu`/`result` because their
    // edges (especially the BOTTOM band) carry UI/CTAs/summary text, which a near-black corner would wash
    // out. vigK multiplies every edge alpha; the vignette also tints toward the lighter `floor` (not pure
    // RIM) off `game` so even the residual darkening stays readable.
    const vigK = mode === 'game' ? 1 : mode === 'menu' ? 0.45 : 0.3;
    const vigTone = mode === 'game' ? RIM : floor;
    const topA = 0.6 * vigK;
    const botA = 0.66 * vigK;
    const sideA = 0.5 * vigK;
    const vg = scene.add.graphics();
    const vh = Math.round(h * 0.2);
    const vw = Math.round(w * 0.17);
    vg.fillGradientStyle(vigTone, vigTone, vigTone, vigTone, topA, topA, 0, 0);
    vg.fillRect(0, 0, w, vh); // top
    vg.fillGradientStyle(vigTone, vigTone, vigTone, vigTone, 0, 0, botA, botA);
    vg.fillRect(0, h - vh, w, vh); // bottom
    vg.fillGradientStyle(vigTone, vigTone, vigTone, vigTone, sideA, 0, sideA, 0);
    vg.fillRect(0, 0, vw, h); // left
    vg.fillGradientStyle(vigTone, vigTone, vigTone, vigTone, 0, sideA, 0, sideA);
    vg.fillRect(w - vw, 0, vw, h); // right
    this.container.add(vg);

    if (motion) {
      scene.events.on(Phaser.Scenes.Events.UPDATE, this.update, this);
      scene.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
        scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
      });
    }
  }

  private drawNebula(w: number, h: number, mode: BackdropMode): void {
    const n = this.nebula!;
    n.clear();
    const cx = this.nebulaBaseX;
    const cy = this.nebulaBaseY;
    // big, very-soft radial blob built from stacked low-alpha discs — a hint of cosmic cloud, no detail.
    const cloudColor = PALETTE.scan;
    const cloudR = Math.max(w, h) * 0.62;
    const cloudA = mode === 'result' ? 0 : mode === 'game' ? 0.008 : 0.007;
    for (let i = 5; i >= 1; i--) {
      n.fillStyle(cloudColor, cloudA * i);
      n.fillCircle(cx, cy, (cloudR * i) / 5);
    }
    // faint wide scanlines — a few big horizontal bands for "screen"/space texture, extremely low alpha.
    const lineA = mode === 'result' ? 0 : mode === 'game' ? 0.022 : 0.02;
    n.fillStyle(PALETTE.surfaceBorder, lineA);
    const bands = 4;
    for (let i = 0; i < bands; i++) {
      const y = h * (0.18 + (i / bands) * 0.64);
      n.fillRect(-w * 0.2, y, w * 1.4, Math.max(1, Math.round(h * 0.006)));
    }
  }

  private drawGrid(w: number, h: number, mode: BackdropMode): void {
    const g = this.grid!;
    g.clear();
    const step = Math.round(w / 9);
    const alpha = mode === 'game' ? 0.065 : 0.07;
    g.lineStyle(1, PALETTE.surfaceBorder, alpha);
    for (let x = step; x < w; x += step) {
      g.lineBetween(x, 0, x, h);
    }
    for (let y = step; y < h; y += step) {
      g.lineBetween(0, y, w, y);
    }
  }

  private drawDepthRibbons(w: number, h: number, mode: BackdropMode): void {
    const r = this.ribbons!;
    r.clear();
    const baseAlpha = mode === 'result' ? 0 : mode === 'game' ? 0.07 : 0.05;
    const lineW = Math.max(1, Math.round(w * 0.004));
    const lanes = [
      { y: h * 0.2, color: PALETTE.scan, alpha: baseAlpha },
      { y: h * 0.48, color: PALETTE.player, alpha: baseAlpha * 0.72 },
      { y: h * 0.76, color: PALETTE.ai, alpha: baseAlpha * 0.58 },
    ];
    for (const lane of lanes) {
      r.lineStyle(lineW, lane.color, lane.alpha);
      r.beginPath();
      r.moveTo(-w * 0.16, lane.y);
      r.lineTo(w * 0.18, lane.y - h * 0.045);
      r.lineTo(w * 0.55, lane.y + h * 0.025);
      r.lineTo(w * 1.16, lane.y - h * 0.07);
      r.strokePath();
    }
  }

  private update(_t: number, dt: number): void {
    this.elapsed += dt;
    const s = this.elapsed / 1000;
    for (const o of this.orbs) {
      o.arc.x = o.baseX + Math.sin(s * 0.18 + o.phase) * o.driftX;
      o.arc.y = o.baseY + Math.cos(s * 0.13 + o.phase * 1.3) * o.driftY;
      o.arc.setScale(1 + 0.12 * Math.sin(s * 0.4 + o.phase));
    }
    // ambient motes drift on slow, individual lissajous paths with a gentle twinkle — never fast/bright.
    for (const m of this.motes) {
      m.arc.x = m.baseX + Math.sin(s * m.speed + m.phase) * m.driftX;
      m.arc.y = m.baseY + Math.cos(s * m.speed * 0.8 + m.phase * 1.2) * m.driftY;
      // twinkle via scale only (base alpha unchanged) to stay cheap and never strobe.
      m.arc.setScale(0.85 + 0.3 * Math.sin(s * 0.6 + m.phase));
    }
    // nebula layer drifts as one very slow parallax mass (far smaller motion than the foreground).
    if (this.nebula) {
      this.nebula.x = Math.sin(s * 0.03) * 18;
      this.nebula.y = Math.cos(s * 0.025) * 12;
    }
    if (this.ribbons) {
      this.ribbons.x = Math.sin(s * 0.04) * 10;
      this.ribbons.y = Math.cos(s * 0.035) * 6;
    }
  }

  destroy(): void {
    this.scene.events.off(Phaser.Scenes.Events.UPDATE, this.update, this);
    this.container.destroy();
  }
}

/** Convenience: attach an atmospheric backdrop to a scene. Returns it so the scene can destroy early. */
export function addBackdrop(scene: Phaser.Scene, mode: BackdropMode): Backdrop {
  return new Backdrop(scene, mode);
}
